import { css, select } from "glamor";
import React from "react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineClose, AiOutlineDown } from "react-icons/ai";
import { GrUndo } from "react-icons/gr";
import { Accordion, Popup, Icon } from "semantic-ui-react";
import Button from "../library/Button";
import { colors, spacing, styles, typographyColors } from "../library/theme";
import deterministicShuffle, { combineSeed } from "../../utils/deterministicShuffle";
import TipTapViewer from "../library/TipTapViewer";

const accordionClass = css({
  backgroundColor: colors.white,
  marginTop: spacing.m,
  border: `1px solid ${colors.greyLight}`,
  borderRadius: spacing.xs,
  overflow: "hidden",
});

const accordionTitleClass = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${spacing.m} !important`,
  backgroundColor: colors.greyLight,
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#E8EAED",
  },
});

const accordionTitleAnsweredClass = css({
  backgroundColor: colors.primaryLighter,
  borderLeft: `4px solid ${colors.primary}`,
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1DA5E8",
    borderLeftColor: "#1DA5E8",
  },
});

const accordionTitleCorrectClass = css({
  backgroundColor: colors.correct,
  borderLeft: `4px solid ${colors.correct}`,
  color: colors.white,
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#05b88a",
    borderLeftColor: "#05b88a",
  },
});

const accordionTitleWrongClass = css({
  backgroundColor: colors.dangerLight,
  borderLeft: `4px solid ${colors.dangerLight}`,
  color: colors.white,
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#FF5A5A",
    borderLeftColor: "#FF5A5A",
  },
});

const titleTextClass = css({
  color: colors.greyShadow,
  fontWeight: 600,
  fontSize: "16px",
  margin: 0,
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const statusIndicatorClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  fontSize: "12px",
});

const answeredIndicatorClass = css(statusIndicatorClass, {
  backgroundColor: colors.primaryLighter,
  color: colors.white,
});

const correctIndicatorClass = css(statusIndicatorClass, {
  backgroundColor: colors.correct,
  color: colors.white,
});

const wrongIndicatorClass = css(statusIndicatorClass, {
  backgroundColor: colors.dangerLight,
  color: colors.white,
});

const accordionContentClass = css({
  padding: `${spacing.m} !important`,
});


const stemContentClass = css(
  styles.bodyTextLarge,
  styles.bodyTextBold,
  {
    fontWeight: "500",
    color: typographyColors.blackLetters,
    lineHeight: 2,
    marginBottom: spacing.m,
    paddingBottom: spacing.m,
    borderBottom: `1px solid ${colors.greyLight}`,
    "> p, * > p": {
      ...styles.bodyTextLarge,
      ...styles.bodyTextBold,
      fontWeight: "500",
      color: typographyColors.blackLetters,
      lineHeight: 2,
    },
  }
);

const choicesContainerClass = css({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const selectedLetters = {
  color: colors.white,
  transition: "all 0.3s",
};

const selectedClass = css({
  "> p": {
    border: `2px solid ${colors.primary}`,
    borderLeft: "none",
  },
  borderColor: colors.primary,
  backgroundColor: colors.white,
  borderLeftWidth: 0,
  transition: "all 0.3s",
});

const selectedDiv = css({
  backgroundColor: colors.primary,
  color: colors.white,
  transition: "all 0.3s",
  "> h3": selectedLetters,
  width: "100%",
});

const choiceContainer = css(
  styles.shadow,
  styles.roundedCorners,
  {
    display: "flex",
    backgroundColor: colors.white,
    overflow: "hidden",
    cursor: "pointer",
    marginTop: spacing.xs,
  },
  select(`&:hover`, selectedClass),
  select(`&:hover > div`, selectedDiv)
);

const choiceValue = css({
  display: "flex",
  minWidth: spacing.xl,
  maxWidth: spacing.xl,
  flex: 1,
  backgroundColor: colors.grey,
  justifyContent: "center",
  alignItems: "center",
});

const choiceValueText = css({
  color: colors.greyShadow,
  fontWeight: "600",
});

// As alternativas (choices) devem forçar o estilo do contexto das questões
const choiceText = css(
  styles.bodyTextLarge,
  styles.bodyTextBold,
  {
    fontWeight: "500",
    color: typographyColors.blackLetters,
    display: "flex",
    padding: `${spacing.l} 60px ${spacing.l} ${spacing.s}`,
    flex: 1,
    border: `2px solid ${colors.white}`,
    borderLeftWidth: 0,
    borderTopRightRadius: spacing.xs,
    borderBottomRightRadius: spacing.xs,
    transition: "all 0.3s",
    "> p, * > p": {
      ...styles.bodyTextLarge,
      ...styles.bodyTextBold,
      fontWeight: "500",
      color: typographyColors.blackLetters,
    },
  }
);


const correctClass = css({
  "> div": {
    backgroundColor: colors.correct,
    borderColor: colors.correct,
  },
  "> p": {
    borderColor: colors.correct,
  },
});

const wrongClass = css({
  "> div": {
    backgroundColor: colors.dangerLight,
    borderColor: colors.dangerLight,
  },
  "> p": {
    borderColor: colors.dangerLight,
  },
});

const disabledClass = css({ pointerEvents: "none" });

const discartedClass = css({
  opacity: 0.5,
  "> p": {
    textDecoration: "line-through",
  },
});

const rightContent = css({
  position: "absolute",
  top: "50%",
  right: spacing.s,
  transform: "translate(0, -50%)",
});

const choiceContainerWrapper = css({
  position: "relative",
});

const accordionArrowIcon = css({
  marginLeft: "auto",
  transition: "transform 0.2s ease",
});

const accordionArrowIconActive = css({
  transform: "rotate(180deg)",
});

const choiceTextWrapperStyle = { backgroundColor: colors.white, width: "100%" }

const numberToCharCode = (num) => String.fromCharCode(65 + num);

export default function QuestionStemAccordion({
  stem,
  stemIndex,
  selectedChoiceId,
  answeredChoice,
  onChoiceSelect,
  readOnly,
  showAnswer,
  isFinished,
  isExam,
  active,
  onToggle,
  isAnswered,
  renderWithoutAccordion = false,
  isDiscarted,
  toggleDiscardChoiceHandler,
  showPercentage,
  questionId,
  displayIndex,
}) {
  const isCorrect = answeredChoice && answeredChoice.correct && showAnswer;
  const isWrong = answeredChoice && !answeredChoice.correct && showAnswer;

  const sortedChoices = deterministicShuffle(
    stem.choices || [],
    combineSeed(questionId, stem.id)
  );

  const getTitleClass = () => {
    const hasAnswer = isAnswered || answeredChoice;
    if (hasAnswer) {
      if (showAnswer && answeredChoice) {
        if (isCorrect) {
          return css(accordionTitleClass, accordionTitleCorrectClass);
        } else if (isWrong) {
          return css(accordionTitleClass, accordionTitleWrongClass);
        }
      } else {
        return css(accordionTitleClass, accordionTitleAnsweredClass);
      }
    }
    return accordionTitleClass;
  };

  const renderStatusIndicator = () => {
    const hasAnswer = isAnswered || answeredChoice;
    if (hasAnswer) {
      if (showAnswer && answeredChoice) {
        if (isCorrect) {
          return (
            <span className={correctIndicatorClass}>
              <AiOutlineCheckCircle size={14} />
            </span>
          );
        } else if (isWrong) {
          return (
            <span className={wrongIndicatorClass}>
              <AiOutlineCloseCircle size={14} />
            </span>
          );
        }
      } else {
        return (
          <span className={answeredIndicatorClass}>
            ✓
          </span>
        );
      }
    }

    return null;
  };

  const titleClassName = getTitleClass();
  const hasAnswer = isAnswered || answeredChoice;
  const titleTextColor = hasAnswer && showAnswer && answeredChoice
    ? (isCorrect ? colors.white : isWrong ? colors.white : colors.greyShadow)
    : hasAnswer ? colors.white : colors.greyShadow;

  const renderChoices = () => (
    <div className={choicesContainerClass}>
      {sortedChoices.map((choice, choiceIndex) => {
        const choiceIsAnswer = answeredChoice && Number(answeredChoice.id) === Number(choice.id);
        const choiceIsSelected = !answeredChoice && Number(selectedChoiceId) === Number(choice.id);
        const choiceIsCorrect = answeredChoice && choice.correct && showAnswer;

        const answerValue = answeredChoice && answeredChoice.correct ? correctClass : wrongClass;
        const answerClass = answeredChoice && showAnswer ? answerValue : selectedClass;

        const choiceIsDiscarted = isDiscarted && isDiscarted(choice.id, stem.id);

        const showAverage = (!!answeredChoice && (!isExam || isFinished)) || readOnly;

        const statusClass = css(
          (choiceIsCorrect || (readOnly && choice.correct)) && correctClass,
          choiceIsAnswer && !readOnly && answerClass,
          ((isExam ? isFinished : answeredChoice) || readOnly || choiceIsDiscarted) && disabledClass,
          choiceIsSelected && selectedClass,
          choiceIsDiscarted && discartedClass
        );

        const className = css(choiceContainer, statusClass);
        const choiceTextClass = css(
          choiceText,
          (choiceIsSelected || (choiceIsAnswer && !showAnswer)) && selectedClass
        );
        const choiceValueDiv = css(
          choiceValue,
          (choiceIsSelected || (choiceIsAnswer && !showAnswer)) && selectedDiv
        );
        const choiceValueLetters = css(
          choiceValueText,
          (choiceIsSelected || (choiceIsAnswer && !showAnswer)) && selectedLetters
        );

        return (
          <div key={choice.id} className={choiceContainerWrapper}>
            <div
              className={className}
              onClick={
                !answeredChoice || (isExam && !isFinished)
                  ? () => onChoiceSelect(stem.id, choice.id)
                  : undefined
              }
            >
              <div className={choiceValueDiv}>
                <h3 className={choiceValueLetters}>
                  {choiceIsCorrect ? (
                    <AiOutlineCheckCircle size={28} />
                  ) : (
                    numberToCharCode(choiceIndex)
                  )}
                </h3>
              </div>
              <div style={choiceTextWrapperStyle}>
                <TipTapViewer content={choice.content} className={choiceTextClass} />
              </div>
            </div>
            {!readOnly && (
              <div className={rightContent}>
                {showAverage ? (
                  <Popup
                    position="right center"
                    content="Não há dados suficientes para calcular as estatísticas da alternativa"
                    disabled={choice?.average !== null && choice?.average !== undefined}
                    trigger={
                      choice?.average === null || choice?.average === undefined ? (
                        <Icon
                          inverted
                          name="question circle"
                          size="large"
                          color="grey"
                        />
                      ) : (
                        showPercentage && `${choice.average}%`
                      )
                    }
                  />
                ) : (
                  !choiceIsSelected && !answeredChoice && toggleDiscardChoiceHandler && (
                    <Popup
                      position="right center"
                      on={["click", "hover"]}
                      content={choiceIsDiscarted ? "Desfazer" : "Marcar como errada"}
                      trigger={
                        <Button
                          toggle
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDiscardChoiceHandler(choice.id, stem.id);
                          }}
                          circular
                          icon={choiceIsDiscarted ? GrUndo : AiOutlineClose}
                        />
                      }
                    />
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (renderWithoutAccordion) {
    return (
      <div>
        <TipTapViewer content={stem.content} className={stemContentClass} />
        {renderChoices()}
      </div>
    );
  }

  return (
    <Accordion className={accordionClass}>
      <Accordion.Title
        className={titleClassName}
        onClick={onToggle}
      >
        <p className={titleTextClass} style={{ color: titleTextColor }}>
          {renderStatusIndicator()}
          Questão {displayIndex || stemIndex + 1}
        </p>
        {!readOnly && (
          <span className={css(accordionArrowIcon, active && accordionArrowIconActive)}>
            <AiOutlineDown size={16} />
          </span>
        )}
      </Accordion.Title>

      <Accordion.Content className={accordionContentClass} active={active}>
        <TipTapViewer content={stem.content} className={stemContentClass} />
        {renderChoices()}
      </Accordion.Content>
    </Accordion>
  );
}
